/**
 * Split a panel whose cut silhouette is several disconnected islands into one
 * piece per island.
 *
 * Some masterfiles group several physical panels under one panel id (the
 * inner panels of a reversible jersey: three separate pieces of fabric in one
 * `innerpanels` clipPath). Nested as one piece they drag the empty space
 * between them onto the sheet, get one label for three parts, and cannot be
 * packed apart. The silhouette already knows better: after the Clipper union
 * each outer ring (with its holes) is one island. Every island becomes its
 * own piece — own window, own silhouette, own label ("innerpanels 2") — while
 * sharing the panel's artwork; the sheet clips each placement to its
 * silhouette, so a piece shows only its own island.
 *
 * The right fix is still in the masterfile (one clipPath per panel); this is
 * the production-side safety net, reported as `panel-split-islands`.
 */
import { insetOutline, pointInPolygon, signedArea, type Polygon } from './labelPath';
import type { PanelWindow } from './panelSplitter';

export interface PieceIsland {
  /** 1-based island number, top-left first. */
  index: number;
  count: number;
  /** Silhouette in the island's own piece-local pt (origin = island bbox). */
  outlineD: string;
  /** Window over the composite (viewBox units) covering just this island. */
  window: PanelWindow;
  widthPt: number;
  heightPt: number;
}

/** Islands smaller than this (pt², ≈0.5 cm²) are stray marks, not panels. */
const MIN_ISLAND_AREA_PT2 = 400;
/** Extra window/silhouette margin around an island, pt. */
const ISLAND_BLEED_PT = 1;

const round = (n: number) => Math.round(n * 1000) / 1000;

function ringsToPathData(rings: Polygon[], dx: number, dy: number): string {
  return rings
    .map(
      (ring) =>
        ring.map(([x, y], i) => `${i ? 'L' : 'M'}${round(x + dx)} ${round(y + dy)}`).join('') + 'Z'
    )
    .join('');
}

/**
 * Null when the silhouette is one connected shape (or unreadable) — the
 * caller keeps the piece as it is. Otherwise one entry per island.
 */
export async function splitPieceIslands(
  outlinePtD: string,
  window: PanelWindow,
  widthPt: number,
  heightPt: number
): Promise<PieceIsland[] | null> {
  if (!outlinePtD || !(widthPt > 0) || !(heightPt > 0)) return null;
  const rings = await insetOutline(outlinePtD, 0);
  if (!rings || rings.length < 2) return null;

  // Outer rings share the orientation of the largest ring; the rest are holes.
  const areas = rings.map(signedArea);
  const outerSign = Math.sign(areas[0]); // insetOutline sorts by |area| desc
  const outers: Array<{ ring: Polygon; holes: Polygon[]; area: number }> = [];
  const holes: Polygon[] = [];
  rings.forEach((ring, i) => {
    if (Math.sign(areas[i]) === outerSign)
      outers.push({ ring, holes: [], area: Math.abs(areas[i]) });
    else holes.push(ring);
  });
  for (const hole of holes) {
    const owner = outers.find((o) => pointInPolygon(hole[0], o.ring));
    (owner ?? outers[0]).holes.push(hole);
  }
  const total = outers.reduce((s, o) => s + o.area, 0);
  const islands = outers.filter((o) => o.area >= Math.max(MIN_ISLAND_AREA_PT2, total * 0.005));
  if (islands.length < 2) return null;

  // Stable numbering: top-left first (row bands of ~2 cm, then x).
  const bboxOf = (o: (typeof islands)[number]) => {
    const xs = o.ring.map((p) => p[0]);
    const ys = o.ring.map((p) => p[1]);
    return {
      x: Math.min(...xs),
      y: Math.min(...ys),
      w: Math.max(...xs) - Math.min(...xs),
      h: Math.max(...ys) - Math.min(...ys),
    };
  };
  const withBox = islands.map((o) => ({ o, box: bboxOf(o) }));
  withBox.sort((a, b) => Math.round(a.box.y / 56) - Math.round(b.box.y / 56) || a.box.x - b.box.x);

  const unitsPerPtX = window.width / widthPt;
  const unitsPerPtY = window.height / heightPt;
  return withBox.map(({ o, box }, i) => {
    const x0 = Math.max(0, box.x - ISLAND_BLEED_PT);
    const y0 = Math.max(0, box.y - ISLAND_BLEED_PT);
    const x1 = Math.min(widthPt, box.x + box.w + ISLAND_BLEED_PT);
    const y1 = Math.min(heightPt, box.y + box.h + ISLAND_BLEED_PT);
    return {
      index: i + 1,
      count: withBox.length,
      outlineD: ringsToPathData([o.ring, ...o.holes], -x0, -y0),
      window: {
        x: window.x + x0 * unitsPerPtX,
        y: window.y + y0 * unitsPerPtY,
        width: (x1 - x0) * unitsPerPtX,
        height: (y1 - y0) * unitsPerPtY,
      },
      widthPt: x1 - x0,
      heightPt: y1 - y0,
    };
  });
}
